
import Vue from 'vue'
import { mapState, mapGetters } from 'vuex'
import ACTION from '../store/ACTION'
import ViewerLoading from '@/components/ViewerLoading.vue'
import LumiereVideo, { VideoOptions, VideoState, seconds } from '@lumiere/video'
import config from '@lumiere/shared/config'
import { VideoVisibility, Video, Feature } from '@lumiere/db/types'
import getHead from '../utils/getHead'
import createThrottledQueue from '../utils/createThrottledQueue'
import EmbedPlayerJS from '../components/commons/EmbedPlayerJS.vue'
import { LUMIERE_EMBED_EVENTS } from '../utils/types'
import viewerAPI from '../services/viewerAPI'

const ENGAGEMENT_BATCHING_DURATION = 5000

export default Vue.extend({
  props: {
    vid: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      videoMomentsQueue: null as ReturnType<typeof createThrottledQueue> | null,
      currentSecond: null as seconds | null,
    }
  },

  computed: {
    ...mapState(['video', 'queryParams']),
    ...mapGetters(['getUser', 'getHeadParams', 'iFrameEmbedData', 'videoURL']),

    allowScrubbing(): boolean {
      return !!this.video?.video?.behaviors?.allowScrubbing
    },
    startFrom(): number {
      const startFrom = this.allowScrubbing ? this.queryParams.time || 0 : 0
      return startFrom
    },
    videoOptions(): VideoOptions {
      return {
        muxData: config.muxEnvKey
          ? {
              user_id: this.$store.state.user?.uid,
              user_is_anonymous: this.$store.state.user?.isAnonymous,
              video_id: this.vid,
              env_key: config.muxEnvKey,
              player_init_time: new Date().toString(),
              video_title: this.video?.video.title,
              video_duration: this.video?.video.duration,
            }
          : undefined,
      }
    },
    isPrivateVideo(): boolean {
      return this.video?.video?.visibility === VideoVisibility.Private
    },
    isEmbed(): boolean {
      return !!this.iFrameEmbedData
    },
    customPoster(): string | undefined {
      if (this.iFrameEmbedData?.poster) {
        const poster = this.iFrameEmbedData.poster
        return decodeURI(poster)
      }
      return undefined
    },
    playEmbedInline(): boolean {
      return parseInt(this.iFrameEmbedData?.playsinline) !== 0
    },
    shouldAutoPlayEmbed(): boolean {
      return parseInt(this.iFrameEmbedData?.autoplay) >= 1
    },
  },

  components: {
    LumiereVideo,
    ViewerLoading,
    EmbedPlayerJS,
  },

  methods: {
    async getVideoSrcPromise(): Promise<string> {
      return viewerAPI.video.getSignedURL({ vid: this.vid })
    },
    async fetchVideo(): Promise<void> {
      await this.$store.dispatch(ACTION.FETCH_VIDEO, {
        vid: this.vid,
        cid: this.$route.params.cid,
      })
    },
    async postFeedback(data: any): Promise<void> {
      await this.$store.dispatch(ACTION.POST_DATA, data)
    },
    onStateChange(state: VideoState) {
      if (state === VideoState.Ended) {
        const returnUrl = this.$store.state.queryParams.returnUrl
        if (returnUrl) {
          let decodedUrl = decodeURI(returnUrl)
          decodedUrl = decodedUrl.replace(/'/gi, '').trim()
          // a valid url must include http(s)? protocol to be navigatable
          const urlRegex =
            /^(?:http(s)?:\/\/)[\w.-]+(?:\.[\w.-]+)+[\w\-._~:/?#[\]@!$&'()*+,;=.]+$/gim

          if (urlRegex.test(decodedUrl)) {
            window.location.href = decodedUrl
          }
        }
      }

      if ([VideoState.Error].includes(state)) {
        this.videoMomentsQueue?.flush()
      }
    },
    onTimeUpdate(time: seconds) {
      this.currentSecond = Math.floor(time)
    },
    initVideoMomentsQueue() {
      if (this.video && !this.videoMomentsQueue) {
        this.videoMomentsQueue = createThrottledQueue((moments) => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.$store.dispatch(ACTION.TRACK_VIDEO_ENGAGEMENT, moments)
        }, ENGAGEMENT_BATCHING_DURATION)
      }
    },
    onBigPlayButtonClicked() {
      if (!this.playEmbedInline) {
        const url = this.videoURL(this.vid)
        // redirect to lumiere
        window.top.location.href = url
      }
    },
  },

  watch: {
    video: {
      handler(video: null | { video: Video; features: Feature[] }) {
        if (video) {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises
          this.$store.dispatch(ACTION.NOTIFY_PARENT_WINDOW, {
            event: LUMIERE_EMBED_EVENTS.VIDEO_PLAYER_RENDERED,
          })
        }
        if (this.isPrivateVideo) {
          const header = getHead(this.getHeadParams())
          document.head.insertAdjacentHTML('afterbegin', header)
        }

        this.initVideoMomentsQueue()
      },
      immediate: true,
    },
    currentSecond(t, t_old) {
      // no if-else-ifs here
      if (t_old === 0) {
        this.videoMomentsQueue?.add({ t: t_old })
      }
      if (t > 0) {
        this.videoMomentsQueue?.add({ t })
      }
      if (t >= this.video.video.duration) {
        this.videoMomentsQueue?.flush()
      }
    },
  },

  async serverPrefetch(): Promise<void> {
    const VUE = this as any
    VUE.fetchVideo()
  },
  mounted() {
    if (this.vid && !this.video) {
      void this.fetchVideo()
    }
    void this.$store.dispatch(ACTION.CREATE_VIEW_RECORD, {
      vid: this.vid,
      cid: this.$route.params.cid,
    })

    void this.$store.dispatch(ACTION.NOTIFY_PARENT_WINDOW, {
      event: LUMIERE_EMBED_EVENTS.VIDEO_PLAYER_MOUNTED,
    })

    this.initVideoMomentsQueue()
    window.addEventListener(
      'beforeunload',
      (_e) => {
        this.videoMomentsQueue?.flush()
        void this.$store.dispatch(ACTION.VIEW_SESSION_ENDED)
      },
      { once: true },
    )
  },
  beforeDestroy() {
    this.videoMomentsQueue?.flush()
  },
})
